/**
 * Runtime axios shim: rewrites cross-subdomain requests to the current origin
 * so the SPA works whether the user lands on `adauraawards.com` or the
 * `www.` variant.
 *
 * Context: the app is built with `REACT_APP_BACKEND_URL` baked in at build
 * time (e.g. `https://adauraawards.com`). When the DNS/CDN redirects users
 * to `www.adauraawards.com`, every credentialed XHR to the baked-in host
 * becomes cross-origin and is blocked by CORS — even though both hostnames
 * point to the same backend.
 *
 * Rule: if the current page's host and the baked-in backend host share the
 * same apex domain (both `adauraawards.com`, ignoring `www.`), we rewrite
 * every axios request that targets the baked-in host to instead hit the
 * current window origin. This eliminates the CORS trip without touching the
 * 27 files that currently reference `process.env.REACT_APP_BACKEND_URL`.
 *
 * Safety: only rewrites when the apex matches — a stray reference to some
 * unrelated third-party host would be left untouched.
 */
import axios from 'axios';

const CONFIGURED_URL = process.env.REACT_APP_BACKEND_URL || '';

const stripWWW = (host) => (host || '').replace(/^www\./i, '');

const resolveTargetHost = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  let cfgHost = '';
  try { cfgHost = new URL(CONFIGURED_URL).host; } catch { return null; }
  if (!cfgHost) return null;
  const curHost = window.location.host;
  if (curHost === cfgHost) return null; // already correct
  if (stripWWW(curHost) !== stripWWW(cfgHost)) return null; // different apex
  return { from: cfgHost, to: curHost };
};

const rewrite = (target, value) => {
  if (!value || typeof value !== 'string') return value;
  return value.includes(target.from) ? value.replace(target.from, target.to) : value;
};

export const installApiOriginShim = () => {
  const target = resolveTargetHost();
  if (!target) return;

  const patch = (instance) => {
    instance.interceptors.request.use((config) => {
      if (config.url) config.url = rewrite(target, config.url);
      if (config.baseURL) config.baseURL = rewrite(target, config.baseURL);
      return config;
    });
  };

  // Patch the default instance for direct `axios.get()` / `axios.post()` calls.
  patch(axios);

  // Wrap `axios.create` so every future instance (nominatorApi, admin `ax`,
  // etc.) automatically gets the same rewrite. This is essential — axios
  // instances do NOT inherit interceptors from the default instance.
  const originalCreate = axios.create.bind(axios);
  axios.create = (config) => {
    const inst = originalCreate(config);
    patch(inst);
    return inst;
  };

  console.info(`[api-shim] rewriting ${target.from} → ${target.to} to keep credentialed requests same-origin.`);
};

// Auto-install at module import time. This module MUST be imported before
// any file that calls `axios.create(...)` — otherwise nominatorApi and
// similar instances would miss the interceptor. `index.js` imports it first.
installApiOriginShim();
